import React, { useEffect, useState } from 'react'
import {
  SchemaFormRenderer,
  type SchemaFormFieldValue,
  type SchemaFormFieldValues,
  type SchemaFormReadOnlyDisplayMode,
} from '@components/pages/FormBuilder/Shared/SchemaFormRenderer'
import type { GroupField } from '../types'
import './FormPreview.css'

/** Pre-filled values for viewing completed forms (field id -> string or boolean for checkbox) */
export type FormPreviewFieldValues = SchemaFormFieldValues

interface FormPreviewProps {
  groups: GroupField[]
  formTitle: string
  /** Pre-filled values when viewing a completed form/document */
  fieldValues?: FormPreviewFieldValues
  patientName?: string
  readOnlyDisplayMode?: SchemaFormReadOnlyDisplayMode
  /** Builder preview: fields can be filled. Answers stay in this component. */
  interactive?: boolean
  /** Latest interactive answers, used when exporting the print PDF. */
  onDraftValuesChange?: (values: SchemaFormFieldValues) => void
}

const FormPreview: React.FC<FormPreviewProps> = ({
  groups,
  formTitle,
  fieldValues,
  patientName,
  readOnlyDisplayMode = 'default',
  interactive = false,
  onDraftValuesChange,
}) => {
  const [draftValues, setDraftValues] = useState<SchemaFormFieldValues>({})

  useEffect(() => {
    if (!interactive || !onDraftValuesChange) return
    onDraftValuesChange(draftValues)
  }, [draftValues, interactive, onDraftValuesChange])

  const handleDraftChange = (
    fieldId: string,
    value: SchemaFormFieldValue,
  ): void => {
    setDraftValues((current) => ({ ...current, [fieldId]: value }))
  }

  return (
    <div className="form-content-inner form-preview">
      <SchemaFormRenderer
        groups={groups}
        formTitle={formTitle}
        fieldValues={interactive ? draftValues : fieldValues}
        readOnly={!interactive}
        richTextReadOnly
        onFieldChange={interactive ? handleDraftChange : undefined}
        patientName={patientName}
        readOnlyDisplayMode={readOnlyDisplayMode}
        useDesignSystemTextArea
      />
    </div>
  )
}

export default FormPreview
